import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { ChevronDownIcon } from '@heroicons/react/24/outline'
import {
  getSecurityPolicy,
  SecurityPolicy,
  SecurityPolicyResponse,
  updateSecurityPolicy,
} from '../services/api'
import { useAuthStore } from '../store/authStore'
import { errorText } from '../utils/errorText'
import { dateTime } from '../utils/date'
import { plural } from '../utils/plural'
import { checkPassword } from '../utils/passwordPolicy'
import { Field, PageHeader, PageShell, SectionCard, Spinner, Toggle } from '../components/ui'

/**
 * Settings > Security: how members of this tenant sign in. Password rules, whether two-factor
 * authentication is required, and how long a sign-in lasts.
 *
 * Every rule applies at the next sign-in or the next time a password is chosen; nothing here
 * touches a session that is already open or a password that is already set. A member of
 * several tenants is held to the strictest of their policies, so what is set here can only
 * tighten what another tenant set, never loosen it.
 */

/** Session lifetimes offered, in minutes. The platform's own lifetime is the longest. */
const LIFETIMES: { minutes: number; label: string }[] = [
  { minutes: 60, label: '1 hour' },
  { minutes: 240, label: '4 hours' },
  { minutes: 480, label: '8 hours' },
  { minutes: 720, label: '12 hours' },
  { minutes: 1440, label: '1 day' },
  { minutes: 2880, label: '2 days' },
]

function lifetimeLabel(minutes: number): string {
  const known = LIFETIMES.find((l) => l.minutes === minutes)
  if (known) return known.label
  if (minutes % 1440 === 0) return plural(minutes / 1440, 'day')
  if (minutes % 60 === 0) return plural(minutes / 60, 'hour')
  return plural(minutes, 'minute')
}

/** The sentences members will see under a password field, for the preview. */
function rulesOf(policy: SecurityPolicy): string[] {
  return checkPassword('', {
    min_length: policy.password_min_length,
    require_uppercase: policy.password_require_uppercase,
    require_lowercase: policy.password_require_lowercase,
    require_digit: policy.password_require_digit,
    require_symbol: policy.password_require_symbol,
    rules: [],
  }).map((c) => c.rule)
}

/** Password lengths offered in the list; any whole number within the limits can also be typed. */
const LENGTHS = [8, 10, 12, 14, 16, 20, 24, 32, 48, 64]

/**
 * A combo box for the minimum password length: type a number or pick one from the list. The
 * text is kept as typed, so the field can be cleared and retyped; only a whole number within
 * the limits reaches the policy, and anything else is reported until it is corrected or the
 * field loses focus, which restores the last valid length.
 */
function LengthCombobox({ value, min, max, onChange, onValidityChange }: {
  value: number
  min: number
  max: number
  onChange: (n: number) => void
  onValidityChange: (valid: boolean) => void
}) {
  const [text, setText] = useState<string | null>(null)
  const [open, setOpen] = useState(false)
  const box = useRef<HTMLDivElement>(null)
  const options = LENGTHS.filter((n) => n >= min && n <= max)
  if (!options.includes(value)) options.push(value)
  options.sort((a, b) => a - b)

  const parsed = text === null ? value : /^\d+$/.test(text) ? Number(text) : NaN
  const valid = Number.isInteger(parsed) && parsed >= min && parsed <= max

  useEffect(() => { onValidityChange(valid) }, [valid, onValidityChange])

  useEffect(() => {
    if (!open) return
    const close = (e: MouseEvent) => {
      if (box.current && !box.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', close)
    return () => document.removeEventListener('mousedown', close)
  }, [open])

  const commit = (n: number) => {
    onChange(Math.min(max, Math.max(min, n)))
    setText(null)
  }

  return (
    <div ref={box} className="relative max-w-[10rem]">
      <div className="relative">
        <input
          type="text"
          inputMode="numeric"
          role="combobox"
          aria-expanded={open}
          aria-controls="password-min-length-options"
          aria-invalid={!valid}
          aria-label="Minimum length"
          className={`input pr-9 ${valid ? '' : 'border-red-500 focus:border-red-500'}`}
          value={text ?? String(value)}
          onChange={(e) => {
            const t = e.target.value.trim()
            setText(t)
            if (/^\d+$/.test(t)) {
              const n = Number(t)
              if (n >= min && n <= max) onChange(n)
            }
          }}
          onBlur={() => { if (!valid) setText(null) }}
          onKeyDown={(e) => {
            if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
              e.preventDefault()
              commit((valid ? parsed : value) + (e.key === 'ArrowUp' ? 1 : -1))
            } else if (e.key === 'Escape') {
              setOpen(false)
            } else if (e.key === 'Enter' && valid) {
              commit(parsed)
              setOpen(false)
            }
          }}
        />
        <button
          type="button"
          tabIndex={-1}
          aria-label="Show common lengths"
          className="absolute inset-y-0 right-0 flex w-8 items-center justify-center text-theme-text-tertiary hover:text-theme-text-primary"
          onClick={() => setOpen((o) => !o)}
        >
          <ChevronDownIcon className="h-4 w-4" />
        </button>
      </div>
      {!valid && (
        <p role="alert" className="mt-1 text-xs text-red-400">
          Enter a whole number from {min} to {max}.
        </p>
      )}
      {open && (
        <ul
          id="password-min-length-options"
          role="listbox"
          aria-label="Common lengths"
          className="absolute z-20 mt-1 max-h-60 w-full overflow-auto rounded-lg border border-theme-border-primary bg-theme-secondary py-1 text-sm shadow-lg"
        >
          {options.map((n) => (
            <li
              key={n}
              role="option"
              aria-selected={n === value}
              className={`cursor-pointer px-3 py-1.5 hover:bg-theme-tertiary ${
                n === value ? 'font-medium text-blue-500' : 'text-theme-text-primary'
              }`}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => { commit(n); setOpen(false) }}
            >
              {n} characters
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

const same = (a: SecurityPolicy, b: SecurityPolicy) =>
  (Object.keys(a) as (keyof SecurityPolicy)[]).every((k) => a[k] === b[k])

export default function SecuritySettings() {
  const { tenantContext } = useAuthStore()
  const [loaded, setLoaded] = useState<SecurityPolicyResponse | null>(null)
  const [draft, setDraft] = useState<SecurityPolicy | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [notice, setNotice] = useState<{ tone: 'ok' | 'bad'; text: string } | null>(null)
  const [lengthValid, setLengthValid] = useState(true)

  useEffect(() => {
    let live = true
    getSecurityPolicy()
      .then((r) => {
        if (!live) return
        setLoaded(r)
        setDraft(r.policy)
      })
      .catch((e) => live && setNotice({ tone: 'bad', text: errorText(e, 'Could not load the security settings.') }))
      .finally(() => live && setLoading(false))
    return () => { live = false }
  }, [])

  const patch = (p: Partial<SecurityPolicy>) => setDraft((d) => (d ? { ...d, ...p } : d))

  const save = async () => {
    if (!draft || !lengthValid) return
    setSaving(true)
    setNotice(null)
    try {
      const r = await updateSecurityPolicy(draft)
      setLoaded(r)
      setDraft(r.policy)
      setNotice({ tone: 'ok', text: 'Security settings saved. They apply at the next sign-in.' })
    } catch (e) {
      setNotice({ tone: 'bad', text: errorText(e, 'Could not save the security settings.') })
    } finally {
      setSaving(false)
    }
  }

  if (loading || !draft || !loaded) {
    return (
      <PageShell>
        {notice && (
          <div role="alert" className="rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-400">
            {notice.text}
          </div>
        )}
        {loading && (
          <div className="flex justify-center py-24 text-theme-text-tertiary">
            <Spinner className="h-6 w-6" />
          </div>
        )}
      </PageShell>
    )
  }

  const { limits } = loaded
  const lifetimes = LIFETIMES.filter((l) => l.minutes >= limits.session_lifetime_minutes.min
    && l.minutes <= limits.session_lifetime_minutes.max)
  // The stored value is always offered, so the select never shows a different lifetime from the
  // one in force.
  if (!lifetimes.some((l) => l.minutes === draft.session_lifetime_minutes)) {
    lifetimes.push({ minutes: draft.session_lifetime_minutes, label: lifetimeLabel(draft.session_lifetime_minutes) })
    lifetimes.sort((a, b) => a.minutes - b.minutes)
  }
  const dirty = !same(draft, loaded.policy)
  const turningMfaOn = draft.mfa_required && !loaded.policy.mfa_required

  return (
    <PageShell>
      <PageHeader
        title="Security"
        subtitle={
          <>
            How members of {tenantContext?.tenant_name || 'this organization'} sign in. Changes apply
            at the next sign-in; sessions already open are not affected. Members who belong to
            several organizations are held to the strictest rules among them.
          </>
        }
        actions={
          <button type="button" className="btn-blue" onClick={save} disabled={!dirty || saving || !lengthValid}>
            {saving ? 'Saving…' : 'Save changes'}
          </button>
        }
      />

      {notice && (
        <div
          role="status"
          className={`mb-4 rounded-lg border px-3 py-2 text-sm ${
            notice.tone === 'ok'
              ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-400'
              : 'border-red-500/30 bg-red-500/10 text-red-400'
          }`}
        >
          {notice.text}
        </div>
      )}

      <div
        role="note"
        aria-label="Scope of these settings"
        className="mb-6 rounded-lg border border-theme-border-primary bg-theme-tertiary px-4 py-3 text-sm text-theme-text-secondary"
      >
        <span className="font-medium text-theme-text-primary">
          Password requirements and two-factor authentication apply to standalone authentication only
        </span>
        : members who sign in with an email address and password. Members who sign in through single
        sign-on are authenticated by your identity provider, whose own password and multi-factor
        policies apply to them. The session lifetime applies to both.
      </div>

      <SectionCard title="Password requirements" className="mb-6">
        <p className="mb-4 text-sm text-theme-text-secondary">
          Checked whenever a member chooses a password: a reset, a change, or accepting an
          invitation. A member whose current password does not meet these rules is asked to
          choose a new one at their next sign-in, including a member who joins from another
          organization with a password that organization allowed.
        </p>
        <div className="grid gap-6 md:grid-cols-2">
          <div>
            <Field
              label="Minimum length"
              hint={`Between ${limits.password_min_length.min} and ${limits.password_min_length.max} characters.`}
            >
              <LengthCombobox
                value={draft.password_min_length}
                min={limits.password_min_length.min}
                max={limits.password_min_length.max}
                onChange={(n) => patch({ password_min_length: n })}
                onValidityChange={setLengthValid}
              />
            </Field>
            <div className="space-y-3">
              {([
                ['password_require_uppercase', 'Require an uppercase letter'],
                ['password_require_lowercase', 'Require a lowercase letter'],
                ['password_require_digit', 'Require a number'],
                ['password_require_symbol', 'Require a symbol'],
              ] as const).map(([key, label]) => (
                <label key={key} className="flex items-center justify-between gap-4 text-sm text-theme-text-primary">
                  <span>{label}</span>
                  <Toggle checked={draft[key]} onChange={(v) => patch({ [key]: v })} label={label} />
                </label>
              ))}
            </div>
          </div>
          <div className="rounded-lg bg-theme-tertiary p-4">
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-theme-text-tertiary">
              Members will see
            </p>
            <ul className="space-y-1 text-sm text-theme-text-secondary" aria-label="Password requirements preview">
              {rulesOf(draft).map((rule) => <li key={rule}>• {rule}</li>)}
            </ul>
          </div>
        </div>
      </SectionCard>

      <SectionCard title="Two-factor authentication" className="mb-6">
        <label className="flex items-start justify-between gap-4">
          <span>
            <span className="block text-sm font-medium text-theme-text-primary">
              Require two-factor authentication for standalone (email and password) sign-ins
            </span>
            <span className="mt-1 block text-sm text-theme-text-secondary">
              A member who has not enrolled sets up an authenticator app at their next sign-in and
              continues. Members cannot turn it off while it is required. Single sign-on users are
              not affected: their identity provider handles their second factor.
            </span>
          </span>
          <Toggle
            checked={draft.mfa_required}
            onChange={(v) => patch({ mfa_required: v })}
            label="Require two-factor authentication for password sign-ins"
          />
        </label>
        {turningMfaOn && loaded.users_without_mfa > 0 && (
          <div role="note" aria-label="Members not yet enrolled" className="mt-4 rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-sm text-amber-500">
            {loaded.users_without_mfa === 1
              ? '1 member who signs in with a password has not enrolled yet and will be asked to at their next sign-in.'
              : `${loaded.users_without_mfa} members who sign in with a password have not enrolled yet and will be asked to at their next sign-in.`}
          </div>
        )}
        <p className="mt-4 text-xs text-theme-text-tertiary">
          A member who loses their phone and their recovery codes is unlocked by an administrator
          resetting their two-factor authentication on the{' '}
          <Link to="/teams/users" className="text-blue-500 hover:text-blue-400">Users</Link> page.
        </p>
      </SectionCard>

      <SectionCard title="Sessions" className="mb-6">
        <Field
          label="Sign members out after"
          hint="Measured from the sign-in, not from the last request: a member is signed out this long after signing in whatever they are doing. Applies to password and single sign-on sessions alike."
        >
          <select
            className="input max-w-xs"
            aria-label="Sign members out after"
            value={draft.session_lifetime_minutes}
            onChange={(e) => patch({ session_lifetime_minutes: Number(e.target.value) })}
          >
            {lifetimes.map((l) => (
              <option key={l.minutes} value={l.minutes}>{l.label}</option>
            ))}
          </select>
        </Field>
      </SectionCard>

      <p className="text-xs text-theme-text-tertiary">
        {loaded.configured && loaded.updated_at
          ? `Last saved ${dateTime(loaded.updated_at)}${loaded.updated_by ? ` by ${loaded.updated_by}` : ''}.`
          : 'Not saved yet: the platform defaults apply.'}
      </p>
    </PageShell>
  )
}
